import React, { useEffect, useState } from 'react'
import {
  addCalendarDays,
  DEFAULT_VALIDITY_DAYS,
  inferValidityPreset,
  parseDateInputValue,
  todayDateInputValue,
  VALIDITY_PRESET_DAYS,
  type ValidityPresetDays,
} from '../../lib/dateUtils'

const PRESET_LABELS: Record<ValidityPresetDays, string> = {
  30: '30 days',
  60: '60 days',
  90: '90 days',
  180: '6 months',
  365: '1 year',
}

interface Props {
  validUntil: string
  onValidUntilChange: (value: string) => void
  anchorDate?: string
  className?: string
}

export default function ProposalValidityFields({
  validUntil,
  onValidUntilChange,
  anchorDate,
  className,
}: Props) {
  const resolvedAnchor = anchorDate || todayDateInputValue()
  const [duration, setDuration] = useState<ValidityPresetDays | 'custom'>(() =>
    validUntil ? inferValidityPreset(validUntil, resolvedAnchor) : DEFAULT_VALIDITY_DAYS
  )

  useEffect(() => {
    if (validUntil) {
      setDuration(inferValidityPreset(validUntil, resolvedAnchor))
    }
  }, [validUntil, resolvedAnchor])

  const handleDurationChange = (value: string) => {
    if (value === 'custom') {
      setDuration('custom')
      return
    }
    const days = parseInt(value, 10) as ValidityPresetDays
    setDuration(days)
    onValidUntilChange(addCalendarDays(todayDateInputValue(), days))
  }

  const displayDate = validUntil
    ? parseDateInputValue(validUntil)?.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }) || validUntil
    : '—'

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Proposal Validity</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Validity period</label>
          <select
            value={duration === 'custom' ? 'custom' : String(duration)}
            onChange={(e) => handleDurationChange(e.target.value)}
            className={inputClass}
          >
            {VALIDITY_PRESET_DAYS.map((days) => (
              <option key={days} value={String(days)}>
                {PRESET_LABELS[days]}
              </option>
            ))}
            <option value="custom">Custom</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Valid until</label>
          {duration === 'custom' ? (
            <input
              type="date"
              value={validUntil}
              onChange={(e) => onValidUntilChange(e.target.value)}
              className={inputClass}
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900">
              {displayDate}
            </div>
          )}
          {duration !== 'custom' && (
            <p className="text-xs text-gray-500 mt-1">
              Calculated from today plus the selected period
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
